import React, { Component } from 'react';
import { getVisibleSelectionRect } from 'draft-js';
import { withStyles, MuiThemeProvider } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import cx from 'classnames';
import services from './services';
import PropTypes from 'prop-types';
import adaTheme from '../../theme';

const getRelativeParent = element => {
  if (!element) {
    return null;
  }

  const position = window
    .getComputedStyle(element)
    .getPropertyValue('position');
  if (position !== 'static') {
    return element;
  }

  return getRelativeParent(element.parentElement);
};

const styles = theme => ({
  root: {
    position: 'absolute',
    transform: 'translate(-50%,-100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.common.black,
    borderRadius: 10,
    boxShadow: theme.shadows[5],
  },
  overridden: {
    borderColor: 'transparent',
    backgroundColor: 'transparent',
  },
});

@observer
class InlineToolbar extends Component {
  static defaultProps = {
    services: services,
  };
  static propTypes = {
    editorState: PropTypes.object.isRequired,
    setEditorState: PropTypes.func.isRequired,
    services: PropTypes.array,
  };

  state = {
    overrideContent: null,
    position: { left: 0, top: 0 },
  };

  constructor(props) {
    super(props);
    this.menu = React.createRef();
  }

  componentDidMount() {
    window.addEventListener('resize', () => this.forceUpdate());
  }

  componentWillUnmount() {
    window.removeEventListener('resize', () => this.forceUpdate());
  }

  handleOverrideContent = content => {
    this.setState({
      overrideContent: content,
    });
  };

  getStyle = () => {
    const { editorState } = this.props;
    const { overrideContent } = this.state;
    if (!editorState) return {};

    const selection = editorState.getSelection();
    // const oneBlock = selection.anchorKey === selection.focusKey;
    // const isEmptySelection = oneBlock && selection.anchorOffset === selection.focusOffset;
    const isVisible =
      (!selection.isCollapsed() && selection.getHasFocus()) || overrideContent;
    return {
      ...this.state.position,
      visibility: isVisible ? 'visible' : 'hidden',
    };
  };

  get selectedBlock() {
    const { editorState } = this.props;
    const startKey = editorState.getSelection().getStartKey();
    return editorState.getCurrentContent().getBlockForKey(startKey);
  }

  setPosition = () => {
    if (!this.menu.current) return;
    const relativeParent = getRelativeParent(this.menu.current.parentElement);
    // const menuHeight = this.menu.clientHeight;
    const parentRect = (
      relativeParent || document.body
    ).getBoundingClientRect();
    const selectionRect = getVisibleSelectionRect(window);
    if (!selectionRect) return;
    let top = selectionRect.top - parentRect.top - 10;
    let left = selectionRect.left - parentRect.left + selectionRect.width / 2;
    left = Math.min(
      Math.max(left, this.menu.current.offsetWidth / 2),
      document.body.clientWidth - this.menu.current.offsetWidth / 2,
    );
    // this prevents some infinite loops
    top = +top.toFixed(0);
    left = +left.toFixed(0);
    // console.table(top, this.state.position.top, left, this.state.position.left);
    if (top !== this.state.position.top || left !== this.state.position.left) {
      this.setState({
        position: {
          top,
          left,
        },
      });
    }
  };

  componentDidMount() {
    this.setPosition();
  }

  componentDidUpdate() {
    this.setPosition();
  }

  render() {
    const { editorState, services, classes } = this.props;
    const { overrideContent: OverrideContent, position } = this.state;
    const blockTypeIsMutable =
      services.findIndex(s => s.blockType === this.selectedBlock.getType()) >
        -1 || this.selectedBlock.getType() === 'unstyled';
    return (
      <MuiThemeProvider theme={adaTheme}>
        <div
          ref={this.menu}
          style={this.getStyle()}
          className={cx(classes.root, {
            [classes.overridden]: OverrideContent,
          })}
        >
          {OverrideContent ? (
            <OverrideContent
              editorState={editorState}
              setEditorState={this.props.setEditorState}
              onOverrideContent={this.handleOverrideContent}
            />
          ) : (
            services.map(Service => (
              <Service
                key={Service.displayName || Service.blockType}
                isHidden={!blockTypeIsMutable}
                editorState={editorState}
                setEditorState={this.props.setEditorState}
                onOverrideContent={this.handleOverrideContent}
              />
            ))
          )}
        </div>
      </MuiThemeProvider>
    );
  }
}

export default withStyles(styles)(InlineToolbar);
